/**
 * AlertsColumn
 *
 * Displays questionnaire alerts for an athlete in a two-column layout (Yesterday | Today).
 * Shows alert badges with tooltips containing full alert details. Handles deduplication
 * of alerts with the same abbreviation on the same day.
 *
 * @component
 * @param {Array} alertInfo - Array of alert objects with date, title, abbr,
 *   description
 * @param {boolean} showQuestionnaireAlerts - If true, shows alert columns;
 *   if false, shows empty state
 * @param {boolean} isFirstRow - Whether this is the first row in the table
 */
import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import IcomoonReact from 'icomoon-react';
import moment from 'moment-timezone';
import { useSelector } from 'react-redux';
import iconSet from '../../../shared/images/teambuildr-selection.json';

const Container = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0;
  width: 100%;

  ${(props) => !props.showQuestionnaireAlerts
    && `
    border: none;
  `}
`;

const DayColumn = styled('div')`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  align-content: flex-start;
  padding: 0;
  gap: 4px;
  flex: 1;
  min-height: 24px;
  border-left: 1px solid #eeeeee;

  &:first-of-type {
    border-left: none;
  }
`;

const AlertsContainer = styled('div')`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
  width: 100%;
  margin-left: 8px;
`;

const AlertBadge = styled('div')`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 0px 4px;
  gap: 8px;
  height: 24px;
  background: #ffcdcd;
  border-radius: 4px;
  font-family: 'Nunito Sans';
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 150%;
  text-align: center;
  color: #444444;
  cursor: pointer;
  transition: all 0.15s ease;
  position: relative;
  white-space: nowrap;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    opacity: 0.9;
  }
`;

const Tooltip = styled('div')`
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  padding: 8px 12px;
  background: #333333;
  color: #ffffff;
  font-size: 12px;
  font-weight: 400;
  border-radius: 4px;
  z-index: 100;
  min-width: 200px;
  max-width: 300px;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s ease;
  text-align: left;
  line-height: 1.4;
  white-space: normal;
  word-wrap: break-word;

  ${AlertBadge}:hover & {
    opacity: 1;
  }

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 5px solid #333333;
  }
`;

const NoAlerts = styled('div')`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: flex-start;
  padding: 0;
  gap: 8px;
  width: 100%;
  height: 40px;
  border-radius: 4px;
  font-family: 'Nunito Sans';
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 150%;
  text-align: center;
  color: #9e9e9e;

  svg {
    flex-shrink: 0;
    width: 16px !important;
    height: 16px !important;
    margin-top: 1px;
  }
`;

const NoAlertsMessage = styled(NoAlerts)`
  justify-content: flex-start;
  text-align: left;
  gap: 4px;
  height: 24px;
  line-height: 16px;
`;

const isToday = (dateString, userTz) => {
  if (!userTz) {
    const today = new Date();
    const todayString = today.toISOString().split('T')[0];
    return dateString === todayString;
  }
  const todayInUserTz = moment().tz(userTz).format('YYYY-MM-DD');
  return dateString === todayInUserTz;
};

const isYesterday = (dateString, userTz) => {
  if (!userTz) {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayString = yesterday.toISOString().split('T')[0];
    return dateString === yesterdayString;
  }
  const yesterdayInUserTz = moment().tz(userTz).subtract(1, 'day').format('YYYY-MM-DD');
  return dateString === yesterdayInUserTz;
};

const buildTooltip = (alert) => {
  const title = alert?.title || alert?.question || '';
  const value = alert?.description ?? alert?.value ?? alert?.answer ?? alert?.response;
  if (value === null || value === undefined || value === '') {
    return title;
  }
  return `${title}: ${String(value)}`;
};

// Create a stable key for each alert
const getKey = (alert, prefix) => alert?.id
  || `${prefix}-${alert?.title || alert?.abbr || 'alert'}-${alert?.date
    || 'nodate'}`;

const getAlertDisplayText = (alert) => alert.abbr || 'Alert';

const AlertsColumn = ({ alertInfo, showQuestionnaireAlerts, isFirstRow }) => {
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const userTz = currentUser?.userTimeZone || currentUser?.userSettings?.timeZone;

  const dedupeAlerts = (alerts) => {
    const seen = new Map();
    return alerts.filter((alert) => {
      const key = alert.questionId || alert.abbr || getAlertDisplayText(alert);
      if (seen.has(key)) {
        return false;
      }
      seen.set(key, true);
      return true;
    });
  };

  const yesterdayAlerts = dedupeAlerts(
    alertInfo?.filter((alert) => isYesterday(alert.date, userTz)) || [],
  );
  const todayAlerts = dedupeAlerts(
    alertInfo?.filter((alert) => isToday(alert.date, userTz)) || [],
  );

  // Helper function to render a day column with alerts
  const renderDayColumn = (alerts, prefix) => (
    <DayColumn>
      <AlertsContainer>
        {alerts.length === 0 ? (
          <NoAlerts>—</NoAlerts>
        ) : (
          alerts.map((alert) => {
            const displayText = getAlertDisplayText(alert);
            return (
              <AlertBadge
                key={getKey(alert, prefix)}
                alertType={displayText}
              >
                <Tooltip>{buildTooltip(alert)}</Tooltip>
                {displayText}
              </AlertBadge>
            );
          })
        )}
      </AlertsContainer>
    </DayColumn>
  );

  // When there are no questionnaire alerts, show empty state message in first row only
  if (!showQuestionnaireAlerts) {
    return (
      <Container showQuestionnaireAlerts={false}>
        {isFirstRow && (
          <NoAlertsMessage>
            <IcomoonReact
              iconSet={iconSet}
              icon='completion-circle'
              size={16}
              color='#9E9E9E'
            />
            There are no alerts from questionnaire responses.
          </NoAlertsMessage>
        )}
      </Container>
    );
  }

  return (
    <Container>
      {renderDayColumn(yesterdayAlerts, 'yesterday')}
      {renderDayColumn(todayAlerts, 'today')}
    </Container>
  );
};

AlertsColumn.propTypes = {
  alertInfo: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      date: PropTypes.string,
      title: PropTypes.string,
      question: PropTypes.string,
      description: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.bool,
      ]),
      value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.bool,
      ]),
      answer: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.bool,
      ]),
      response: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.bool,
      ]),
      abbr: PropTypes.string,
    }),
  ),
  showQuestionnaireAlerts: PropTypes.bool,
  isFirstRow: PropTypes.bool,
};

AlertsColumn.defaultProps = {
  alertInfo: [],
  showQuestionnaireAlerts: true,
  isFirstRow: false,
};

export default AlertsColumn;
